import { useContext, useEffect, useMemo, useState } from "react";
import { Form, Modal, Flex, Input, Typography } from "antd";
import dayjs from "dayjs";

import NumberInput from "./components/NumberInput";
import AssignTo from "@/components/ui/AssignTo";
import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";

import { UserContext } from "@/context/UserContext";

import { getPluralizedWord } from "@/utils";
import {
  MAX_NUMBER_INPUT_VALUE,
  NUMBER_INPUT_RANGE_MESSAGE,
  parseNumberInput,
} from "@/utils/fieldValueConverter";

import { getUnitEmployees } from "@/api/employees";
import { createItem, updateItem } from "@/api/laborCosts";

const { Text } = Typography;

const mapMembersToOptions = (members) => {
  return members.map((item) => {
    const { lastname, firstname, patronymic } = item;
    return {
      ...item,
      label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
    };
  });
};

const ActualLaborCostsModal = ({
  data,
  task_uuid,
  unit_uuid,
  visible,
  onCancel,
  onSave,
  isAdmins = true,
  text = "",
}) => {
  const { user } = useContext(UserContext);

  const initialValues = useMemo(
    () => ({
      uuid: data?.uuid || null,
      task_uuid: task_uuid,
      employee_uuid: data?.employee_uuid || user?.employee_uuid,
      hours: data?.hours || 1,
      comment: data?.comment || "",
      recorded_at: data?.recorded_at ? dayjs(data.recorded_at) : dayjs(),
    }),
    [data, task_uuid, user],
  );

  // Форма
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // При инициализации компонента запрашиваем список исполнителей
  useEffect(() => {
    if (isAdmins) {
      fetchUserList(unit_uuid);
    }

    // eslint-disable-next-line
  }, [data, isAdmins]);

  const fetchUserList = async (unit_uuid) => {
    try {
      const { members } = await getUnitEmployees({
        ...(unit_uuid && { unit_uuid }),
      });

      // Fixme: Может уже придумаем другой способ как обходить удаленных из РГ сотрудников?
      const employees = [...members];

      if (
        data?.employeer &&
        !employees.find((item) => item.uuid === data.employeer.uuid)
      ) {
        employees.push(data.employeer);
      }

      // если текущий пользователь админ и его нет в списке
      if (
        isAdmins &&
        user?.employee_uuid &&
        !employees.find((item) => item.uuid === user.employee_uuid)
      ) {
        employees.push({
          ...user,
          uuid: user.employee_uuid,
        });
      }

      setEmployeeOptions(mapMembersToOptions(employees));
    } catch (error) {
      console.log(error);
    }
  };

  const [employeeOptions, setEmployeeOptions] = useState(
    data?.employeer
      ? [
          {
            ...data.employeer,
            label: `${data.employeer.lastname || ""} ${
              data.employeer.firstname || ""
            } ${data.employeer.patronymic || ""}`,
          },
        ]
      : [],
  );

  const setMeEmployeer = async () => {
    form.setFieldsValue({
      employee_uuid: user.employee_uuid,
    });
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    const recorded_at = values.recorded_at
      ? values.recorded_at.format("YYYY-MM-DD")
      : null;

    const payload = {
      ...values,
      uuid: data?.uuid || null,
      task_uuid: task_uuid,
      employee_uuid: values.employee_uuid || user?.employee_uuid,
      hours: Number(values.hours),
      recorded_at,
    };

    try {
      let res = null;
      if (data) {
        res = await updateItem({ ...data, ...payload }, isAdmins);
      } else {
        res = await createItem(payload, isAdmins);
      }

      onSave(res);
      form.resetFields();
    } catch (error) {
      setError(error.message || "Не удалось сохранить трудозатраты");
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    setError(null);
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  const hoursRaw = Form.useWatch("hours", form);

  const hoursNumber =
    typeof hoursRaw === "number"
      ? hoursRaw
      : Number((hoursRaw ?? "").toString().replace(",", ".")) || null;

  const hoursInt = Math.trunc(hoursNumber);

  const hoursLabel = getPluralizedWord(hoursInt, ["час", "часа", "часов"]);

  const employeeValue = Form.useWatch("employee_uuid", form);
  const showAssignAction = employeeValue !== user?.employee_uuid;

  return (
    <Modal
      onOk={onOk}
      onCancel={onCancel}
      title={
        data
          ? "Редактировать фактические трудозатраты"
          : "Добавить фактические трудозатраты"
      }
      open={visible}
      width={550}
      okText={data ? "Сохранить" : "Добавить"}
      cancelText="Назад"
      style={{ paddingRight: 20 }}
      okButtonProps={{ loading: isLoading }}
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleSave}
      >
        <Flex vertical gap={8}>
          <Flex
            align="center"
            justify="space-between"
            style={{ width: "100%" }}
          >
            <Form.Item
              label="Количество часов"
              required
              style={{ marginBottom: 0 }}
            >
              <Flex align="center" gap={6}>
                <Form.Item
                  name="hours"
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, введите трудозатраты",
                    },
                    {
                      validator(_, value) {
                        if (
                          value === null ||
                          value === undefined ||
                          value === ""
                        ) {
                          return Promise.resolve();
                        }

                        const numericValue = parseNumberInput(value);
                        if (
                          Number.isFinite(numericValue) &&
                          numericValue >= 0 &&
                          numericValue <= MAX_NUMBER_INPUT_VALUE
                        ) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error(NUMBER_INPUT_RANGE_MESSAGE),
                        );
                      },
                    },
                  ]}
                >
                  <NumberInput
                    style={{ width: 80 }}
                    placeholder="0"
                    min={0}
                    max={MAX_NUMBER_INPUT_VALUE}
                    onStep={(value) => {
                      form.setFieldsValue({ hours: value });
                    }}
                  />
                </Form.Item>

                <span>{hoursLabel}</span>
              </Flex>
            </Form.Item>

            <Form.Item
              name="recorded_at"
              label="Дата исполнения"
              style={{ marginBottom: 0 }}
            >
              <DigitsDatePicker
                style={{ minWidth: 150 }}
                allowClear={false}
              />
            </Form.Item>
          </Flex>

          <Form.Item
            name="comment"
            label="Комментарий"
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 10 }} />
          </Form.Item>

          {isAdmins && !data && (
            <AssignTo showAction={showAssignAction} onChange={setMeEmployeer}>
              <Form.Item name="employee_uuid" label="Сотрудник">
                <CustomSelect
                  placeholder="Начните вводить"
                  options={employeeOptions}
                  showSearch
                  fieldNames={{ value: "uuid" }}
                  optionFilterProp="label"
                  onChange={(value) => {
                    form.setFieldsValue({
                      employee_uuid:
                        value || data?.employee_uuid || user?.employee_uuid,
                    });
                  }}
                  disabled={!isAdmins}
                />
              </Form.Item>
            </AssignTo>
          )}

          {text && <Text>{text}</Text>}

          {error && <Text type="danger">{error}</Text>}
        </Flex>
      </Form>
    </Modal>
  );
};

export default ActualLaborCostsModal;
